import React, { useState, useEffect } from "react";
import { Link, navigate } from "gatsby";
import { useLocation } from "@reach/router";
import scrollTo from "gatsby-plugin-smoothscroll";
import { useServiceTherapies } from "../../hooks/Service-Queries";
import logo from '../../images/logo.png';
import logo_full from '../../images/logo-full.png';
import { StaticImage } from "gatsby-plugin-image";

export const Nav = () => {
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isAnimating, setIsAnimating] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isMobileServicesOpen, setIsMobileServicesOpen] = useState(false);
  const location = useLocation();

  const delays = ['delay-75', 'delay-150', 'delay-200', 'delay-300', 'delay-500', 'delay-700', 'delay-1000'];

  const therapies = useServiceTherapies();

  // handling blur on scroll to show full video
  const [scrolled, setScrolled] = useState(false);

  const isHomePage = location.pathname === "/";

  useEffect(() => {
    const handleScrolls = () => {
      if (isMobileOpen) {
        setIsMobileOpen(false);
        setIsMobileServicesOpen(false);
      }
    };

    window.addEventListener("scroll", handleScrolls);
    return () => window.removeEventListener("scroll", handleScrolls);
  }, [isMobileOpen]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 200);
    };

    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const handleHomeClick = (e: React.MouseEvent) => {
    e.preventDefault();
    setIsMobileOpen(false);
    if (location.pathname === "/") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      navigate("/");
    }
  };

  const handleMobileLinkClick = () => {
    setIsMobileOpen(false);
    setIsMobileServicesOpen(false);
  };

  useEffect(() => {
    if (isServicesOpen) {
      const t = setTimeout(() => setIsAnimating(true), 10);
      return () => clearTimeout(t);
    } else {
      setIsAnimating(false);
    }
  }, [isServicesOpen]);

  const navbar_bg_classes = () => {
    if (isHomePage) {
      return scrolled ? '!bg-sandLight [&>li]:!text-brownDark shadow-2xl [&>p]:text-darkBrown ' : '!bg-transparent [&>li]:text-brownDark [&>li]:text-white z-20';
    }
    return '!bg-sandLight ';
  }

  const mobile_service_menu_bg_classes = () => {
    if (isHomePage) {
      return scrolled ? 'bg-sandLight [&>p]:!text-darkBrown' : 'backdrop-blur bg-slate-900/20';
    }
    return 'bg-sandLight';
  }

  const mobile_service_menu_classes = () => {
    if (isHomePage) {
      return scrolled ? '[&>li>*]:text-brownDark [&>li>*]:text-lg [&>li]:hover:text-brownDarker' : '[&>li>*]:text-white [&>li>*]:text-lg [&>li]:text-brownDark';
    }
    return '[&>li>*]:text-brownDark [&>li>*]:text-lg [&>li]:hover:text-brownDarker';
  }

  const title_submenu_classes = () => {
    if (isHomePage) {
      return scrolled ? 'text-brownDarker' : 'text-white';
    }
    return 'text-brownDarker';
  }


  return (
    <nav className={`fixed z-30 transition duration lg:!h-[90px] w-screen ${navbar_bg_classes()} md:py-4`}>
      <div
        className={`absolute bottom-0 left-1/2 -translate-x-1/2 ${isMobileOpen ? 'w-[100%]' : 'w-[90%]'} h-[2px] z-10 transition-all duration-500 ease-in-out
    ${isHomePage && !scrolled ? 'bg-white opacity-100' : 'opacity-0'}
  `}
      ></div>
      <div className="container flex flex-wrap items-center justify-between p-4 mx-auto">
        <button
          type="button"
          className="relative inline-flex items-center justify-center w-10 h-10 p-2 text-sm text-gray-500 rounded-lg right-4 md:hidden"
          aria-controls="mobile-menu"
          aria-expanded={isMobileOpen}
          onClick={() => setIsMobileOpen((prev) => !prev)}
        >
          <span className="sr-only">Open main menu</span>
          <svg className="w-5 h-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 17 14">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 16 14"
              strokeWidth="2"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                stroke={
                  !isHomePage
                    ? "currentColor"       // always gray on non-home pages
                    : scrolled
                      ? "currentColor"       // black or gray depending on nav color
                      : "white"              // white when on hero
                }
                d="M1 1h15M1 7h15M1 13h15"
              />
            </svg>

          </svg>
        </button>

        {/* Desktop Nav */}
        <div className="!z-30 hidden w-full md:block md:w-auto" id="navbar-dropdown">
          <ul className={`flex flex-col p-0 mt-4 font-medium border border-gray-100 !shadow-none rounded-lg navbar ${navbar_bg_classes()} md:flex-row md:space-x-8 md:mt-0 md:border-0`}>
            <li className="pl-8">
              <a href="/" onClick={handleHomeClick}>Home</a>
            </li>
            <li>
              <Link to="/about">About</Link>
            </li>
            <li className="relative">
              <div className="inline-block">
                <button
                  onMouseEnter={() => setIsServicesOpen(true)}
                  className={`flex items-center py-2 pl-3 pr-2 rounded md:p-0 ${navbar_bg_classes()}`}
                >
                  Services
                  <svg
                    className="w-2.5 h-2.5 ml-2.5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 10 6"
                  >
                    <path
                      stroke="currentColor"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth="2"
                      d="m1 1 4 4 4-4"
                    />
                  </svg>
                </button>

                {isServicesOpen && (
                  <ul
                    onMouseLeave={() => setIsServicesOpen(false)}
                    className={`absolute left-0 z-10 p-4 !top-10 bg-white border rounded-md shadow-lg w-[315px] transition-all duration-200 ease-out
                      ${isAnimating ? "opacity-100 translate-y-0" : "opacity-0 translate-y-2"}
                    `}
                  >
                    {therapies.map((therapy) => (
                      <li className="!my-2 hover:!no-underline" key={therapy.link}>
                        <Link
                          to={`${therapy.link}`}
                          className={`block py-0 transition duration-200 transform text-brownDark hover:translate-x-1 hover:text-brownDarker`}
                        >
                          {therapy.name}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </li>
            <li>
              <button onClick={() => scrollTo("#contact-form")}>Contact</button>
            </li>
          </ul>
        </div>
      </div>


      <div
        className={`${mobile_service_menu_bg_classes()} transition-all duration-300 ease-in-out overflow-hidden md:hidden  px-6 border-t border-gray-300
        ${isMobileOpen ? 'max-h-[500px] opacity-100 pt-4 pb-6' : 'max-h-0 opacity-0'}
      `}>
        <p className={`${title_submenu_classes()} !text-[24px] mx-auto text-center`}>Health And Bloom</p>
        <ul className={`!mb-0 flex flex-col items-start space-y-8 [&>li]:m-0 ${mobile_service_menu_classes()}`}>
          <li>
            <a href="/" onClick={handleHomeClick}>Home</a>
          </li>
          <li>
            <Link to="/about" onClick={handleMobileLinkClick}>About</Link>
          </li>
          <li>
            <button
              className="flex items-center justify-start w-full gap-2"
              onClick={() => setIsMobileServicesOpen(!isMobileServicesOpen)}
            >
              Services
              <svg className={`w-4 h-4 transition-transform ${isMobileServicesOpen ? 'rotate-180' : ''}`} fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </button>
            <div
              className={`overflow-hidden transition-all duration-500 ease-in-out ${isMobileServicesOpen ? 'max-h-[500px] mt-4' : 'max-h-0'
                }`}
            >
              <ul className="ml-4 space-y-2 [&>li]:m-0">
                {therapies.map((therapy, i) => (
                  <li
                    key={therapy.link}
                    className={`transition duration-300 ease-out transform ${isMobileServicesOpen
                      ? `opacity-100 translate-x-0 ${delays[i] ?? 'delay-1000'}`
                      : 'opacity-0 -translate-x-2'
                      }`}
                  >
                    <Link
                      to={therapy.link}
                      onClick={handleMobileLinkClick}
                      className={`block ${scrolled ? 'text-brownDarker' : "!text-white"} ${!isHomePage && '!text-brownDark'}`}
                    >
                      {therapy.name}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          </li>
          <li>
            <button
              onClick={() => {
                scrollTo("#contact-form");
                setIsMobileOpen(false);
              }}
            >
              Contact
            </button>
          </li>
        </ul>
      </div>
    </nav >
  );
};
